import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ButtonDirective, WhButtonComponent, WhStatusPillComponent } from '@wheere-front/shared-ui';
import { errorMessage } from '@wheere-front/shared-util';
import { BatchExecutor, TrackerBatchOutcome, batchFailureKey, loraNextItv, retryableIds } from '../util/tracker-batch';
import { BatchLinkContext, batchFailureLinks } from '../util/tracker-batch-links';

/** Un appareil du lot, tel que le récapitulatif l'affiche. */
export interface BatchDevice {
  id: string;
  name: string;
  deviceId?: string;
  /** En cours de log (`Tracker.logSessionStarted`) : déclenche la popup avant l'envoi. */
  logging?: boolean;
  /** Libellé déjà traduit du groupe des réussis (« déplacés de A à B »…). */
  group?: string;
  /** Site occupé avant l'envoi : celui dont un DETACH renvoie vers l'onglet LoRa. */
  siteId?: string | null;
}

type Phase = 'logs' | 'running' | 'summary';

/** Places LoRa par seconde de période (ADR-022) : capacité = 80 × itv. */
const PLACES_PER_SECOND = 80;

/**
 * Exécution d'un lot de trackers et son récapitulatif unique (maquette trackers-en-lot, écarts du
 * 2026-10-05) : popup « logs en cours » avant l'envoi, un spinner pendant l'appel unique, puis les
 * réussis groupés, chaque échec avec une phrase lisible, et « Réessayer les N échecs ».
 * Partagé par Manage et la fiche site.
 */
@Component({
  selector: 'lib-tracker-batch-run',
  standalone: true,
  imports: [TranslocoModule, ButtonDirective, WhButtonComponent, WhStatusPillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div
        class="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800"
        [class]="phase() === 'summary' ? 'max-w-2xl' : 'max-w-md'">
        @switch (phase()) {
          @case ('logs') {
            <div class="border-b border-gray-200 px-5 py-4 dark:border-slate-700">
              <div class="text-base font-bold text-slate-900 dark:text-slate-100">
                {{ 'trackerBatch.logs.title' | transloco }}
              </div>
            </div>
            <div class="flex flex-col gap-3 px-5 py-4 text-sm text-slate-700 dark:text-slate-200">
              <p>{{ 'trackerBatch.logs.body' | transloco: { n: loggingPending().length } }}</p>
              <div class="flex flex-wrap gap-1.5">
                @for (d of loggingPending(); track d.id) {
                  <span
                    class="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                    >{{ d.name }}</span
                  >
                }
              </div>
            </div>
            <div class="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-slate-700">
              <wh-button variant="ghost" (click)="cancelLogs()">{{ 'trackerBatch.logs.cancel' | transloco }}</wh-button>
              <wh-button variant="destructive" (click)="confirmLogs()">{{
                'trackerBatch.logs.confirm' | transloco
              }}</wh-button>
            </div>
          }
          @case ('running') {
            <div class="flex flex-col items-center gap-3 px-5 py-10 text-center" aria-live="polite">
              <span
                class="border-t-primary-500 size-8 animate-spin rounded-full border-4 border-gray-200 dark:border-slate-600"
                aria-hidden="true"></span>
              <div class="text-sm font-bold text-slate-900 dark:text-slate-100">
                {{ 'trackerBatch.run.sending' | transloco }}
              </div>
              <div class="text-xs text-slate-500 dark:text-slate-400">
                {{ 'trackerBatch.run.sendingSub' | transloco: { n: sentCount() } }}
              </div>
            </div>
          }
          @case ('summary') {
            <div class="border-b border-gray-200 px-5 py-4 dark:border-slate-700">
              <div class="text-base font-bold text-slate-900 dark:text-slate-100">
                {{ 'trackerBatch.run.summaryTitle' | transloco: { title: title() } }}
              </div>
              <div class="text-xs text-slate-500 dark:text-slate-400">
                {{ (failures().length ? 'trackerBatch.run.someFailed' : 'trackerBatch.run.allOk') | transloco }}
              </div>
            </div>
            <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
              <div class="flex flex-wrap items-center gap-2">
                <wh-status-pill tone="ok">{{
                  'trackerBatch.run.succeeded' | transloco: { n: succeededCount() }
                }}</wh-status-pill>
                @if (failures().length) {
                  <wh-status-pill tone="error">{{
                    'trackerBatch.run.failed' | transloco: { n: failures().length }
                  }}</wh-status-pill>
                }
                <span class="text-xs text-slate-500 dark:text-slate-400">{{
                  'trackerBatch.run.outOf' | transloco: { total: sentCount() }
                }}</span>
              </div>

              @if (succeededGroups().length) {
                <details
                  class="rounded-lg border border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10"
                  [open]="!failures().length">
                  <summary class="flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm">
                    <b class="text-slate-900 dark:text-slate-100">{{
                      'trackerBatch.run.succeededGroup' | transloco: { n: succeededCount() }
                    }}</b>
                  </summary>
                  <div class="flex flex-col gap-2 border-t border-green-200 px-3 py-2.5 dark:border-green-500/30">
                    @for (g of succeededGroups(); track g.label) {
                      <div class="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                        @if (g.label) {
                          <span class="shrink-0 text-xs font-bold text-slate-600 sm:w-48 dark:text-slate-300"
                            >{{ g.devices.length }} {{ g.label }}</span
                          >
                        }
                        <div class="flex flex-wrap gap-1.5">
                          @for (d of g.devices; track d.id) {
                            <span
                              class="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-bold text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                              >{{ d.name }}</span
                            >
                          }
                        </div>
                      </div>
                    }
                  </div>
                </details>
              }

              @if (failures().length) {
                <div>
                  <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                    {{ 'trackerBatch.run.failedGroup' | transloco: { n: failures().length } }}
                  </div>
                  <div class="mt-2 overflow-hidden rounded-lg border border-red-200 dark:border-red-500/40">
                    <div class="overflow-x-auto">
                      <table class="w-full min-w-[480px] border-collapse text-left text-sm">
                        <thead>
                          <tr class="bg-red-50 dark:bg-red-500/10">
                            <th
                              class="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                              {{ 'trackerBatch.run.colDevice' | transloco }}
                            </th>
                            <th
                              class="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                              {{ 'trackerBatch.run.colWhat' | transloco }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (f of failures(); track f.trackerId) {
                            <tr class="border-t border-gray-100 dark:border-slate-700">
                              <td class="w-36 px-3 py-2 align-top">
                                <span class="font-bold text-slate-900 dark:text-slate-100">{{ f.name }}</span>
                                @if (f.deviceId) {
                                  <div class="font-mono text-[11px] text-gray-400">{{ f.deviceId }}</div>
                                }
                              </td>
                              <td class="px-3 py-2 align-top">
                                <div class="text-slate-900 dark:text-slate-100">
                                  {{ f.key ? (f.key | transloco) : f.message }}
                                </div>
                                @if (f.key && f.message) {
                                  <div class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{{ f.message }}</div>
                                }
                                <div class="mt-0.5 font-mono text-[11px] text-gray-400">{{ f.code }}</div>
                                @if (f.link; as link) {
                                  <a
                                    libBtn
                                    variant="outline"
                                    size="xs"
                                    class="mt-1.5"
                                    data-failure-link
                                    [href]="link.path"
                                    target="_blank"
                                    rel="noopener"
                                    >{{ link.labelKey | transloco }}</a
                                  >
                                }
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              }

              @if (loraFull(); as full) {
                <div
                  class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  <p>
                    {{
                      'trackerBatch.run.loraFull'
                        | transloco: { n: full.ids.length, itv: full.itv, places: full.places }
                    }}
                  </p>
                  @if (widen()) {
                    <div class="mt-2">
                      <wh-button variant="destructive" size="sm" [loading]="widening()" (click)="onWiden(full)">{{
                        'trackerBatch.run.widen' | transloco: { itv: full.itv }
                      }}</wh-button>
                    </div>
                  }
                </div>
              }

              @if (failures().length) {
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ 'trackerBatch.run.selectionNote' | transloco }}
                </p>
              }
            </div>
            <div class="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-slate-700">
              <wh-button [variant]="failures().length ? 'ghost' : 'default'" (click)="closed.emit()">{{
                'trackerBatch.run.close' | transloco
              }}</wh-button>
              @if (retryIds().length) {
                <wh-button (click)="retry()">{{
                  retryIds().length > 1
                    ? ('trackerBatch.run.retry' | transloco: { n: retryIds().length })
                    : ('trackerBatch.run.retryOne' | transloco)
                }}</wh-button>
              }
            </div>
          }
        }
      </div>
    </div>
  `,
})
export class TrackerBatchRunComponent implements OnInit {
  readonly #messages = inject(MessageService);
  readonly #transloco = inject(TranslocoService);

  /** Nom de l'action, déjà traduit (« Déplacer », « Détacher »…). */
  readonly title = input.required<string>();
  /** Appareils à envoyer, dans l'ordre. */
  readonly devices = input.required<BatchDevice[]>();
  readonly execute = input.required<BatchExecutor>();
  /** MOVE seulement : allonge la période du site (`PUT /sites/{id}/lora/itv`), `true` si fait. */
  readonly widen = input<((itv: number) => Promise<boolean>) | null>(null);
  /**
   * Inside seulement : action et site cible du lot, pour le bouton « aller corriger » de chaque
   * échec (page inside dans un nouvel onglet). `null` : aucun bouton.
   */
  readonly links = input<BatchLinkContext | null>(null);

  /** Après chaque envoi abouti (premier ou réessai) : l'appelant recharge et met à jour la sélection. */
  readonly finished = output<TrackerBatchOutcome>();
  readonly closed = output<void>();

  protected readonly phase = signal<Phase>('running');
  protected readonly outcome = signal<TrackerBatchOutcome | null>(null);
  protected readonly sentCount = signal(0);
  protected readonly widening = signal(false);
  readonly #pending = signal<string[]>([]);
  /** Dernier choix « arrêter les logs » : un réessai le reprend. */
  #stopLogging = false;

  readonly #byId = computed(() => new Map(this.devices().map(d => [d.id, d])));

  protected readonly loggingPending = computed(() =>
    this.#pending()
      .map(id => this.#device(id))
      .filter(d => d.logging || this.#failedWith(d.id, 'TRACKER_LOGGING_ACTIVE'))
  );

  protected readonly succeededCount = computed(() => this.outcome()?.succeeded.length ?? 0);

  protected readonly succeededGroups = computed(() => {
    const groups = new Map<string, BatchDevice[]>();
    for (const id of this.outcome()?.succeeded ?? []) {
      const d = this.#device(id);
      const label = d.group ?? '';
      groups.set(label, [...(groups.get(label) ?? []), d]);
    }
    return [...groups].map(([label, devices]) => ({ label, devices }));
  });

  protected readonly failures = computed(() => {
    const failed = this.outcome()?.failed ?? [];
    const ctx = this.links();
    const links = ctx ? batchFailureLinks(failed, ctx, id => this.#device(id).siteId) : [];
    return failed.map((f, i) => {
      const d = this.#device(f.trackerId);
      return { ...f, name: d.name, deviceId: d.deviceId, key: batchFailureKey(f.code), link: links[i] ?? null };
    });
  });

  /** Refus « site plein » : la période proposée par le serveur et la place qu'elle offrirait. */
  protected readonly loraFull = computed(() => {
    const full = this.failures().filter(f => f.code === 'LORA_SLOTS_FULL');
    const itv = full.map(f => loraNextItv(f.message)).find((v): v is number => v !== null);
    if (!full.length || !itv) return null;
    return { ids: full.map(f => f.trackerId), itv, places: itv * PLACES_PER_SECOND };
  });

  ngOnInit(): void {
    this.#start(this.devices().map(d => d.id));
  }

  protected confirmLogs(): void {
    void this.#send(this.#pending(), true);
  }

  protected cancelLogs(): void {
    if (this.outcome()) this.phase.set('summary');
    else this.closed.emit();
  }

  /**
   * Échecs renvoyés par « Réessayer » : tous sauf LORA_SLOTS_FULL, que seul l'allongement de la
   * période rattrape (maquette ; décision d'Eddy du 2026-10-06).
   */
  protected readonly retryIds = computed(() => {
    const outcome = this.outcome();
    if (!outcome) return [];
    const full = new Set(outcome.failed.filter(f => f.code === 'LORA_SLOTS_FULL').map(f => f.trackerId));
    return retryableIds(outcome).filter(id => !full.has(id));
  });

  protected retry(): void {
    const ids = this.retryIds();
    if (ids.length) this.#start(ids);
  }

  protected async onWiden(full: { ids: string[]; itv: number }): Promise<void> {
    const widen = this.widen();
    if (!widen || this.widening()) return;
    this.widening.set(true);
    try {
      const ok = await widen(full.itv);
      if (!ok) return;
    } finally {
      this.widening.set(false);
    }
    this.#start(full.ids);
  }

  #start(ids: string[]): void {
    this.#pending.set(ids);
    if (this.loggingPending().length > 0) {
      this.phase.set('logs');
      return;
    }
    void this.#send(ids, this.#stopLogging);
  }

  async #send(ids: string[], stopLogging: boolean): Promise<void> {
    this.#stopLogging = stopLogging;
    this.sentCount.set(ids.length);
    this.phase.set('running');
    try {
      const outcome = await this.execute()(ids, stopLogging);
      this.outcome.set(outcome);
      this.finished.emit(outcome);
      this.phase.set('summary');
    } catch (e) {
      this.#messages.add({
        severity: 'error',
        key: 'default',
        detail: errorMessage(e, this.#transloco.translate('trackerBatch.run.globalError')),
      });
      if (this.outcome()) this.phase.set('summary');
      else this.closed.emit();
    }
  }

  #device(id: string): BatchDevice {
    return this.#byId().get(id) ?? { id, name: id };
  }

  #failedWith(id: string, code: string): boolean {
    return !!this.outcome()?.failed.some(f => f.trackerId === id && f.code === code);
  }
}
